/* eslint-disable max-lines */
import { Component, Input, ViewChild } from '@angular/core';
import { RegistrationService } from '../../registration.service';
import { RegistrationState, SaveUserPayload } from '../../definitions';
import { RegistrationFactory } from '../../registration.factory';
import { ChangeVerifyRole, CountryCode, ModalButtonId, RemovalSubmissionButton, SuccessFailureBtn, UseVerifiedOrEditAddress, UserDetailsConstant, WarningMessage, countries, provinces, states } from '../../../../services/shared/shared-enums';
import { canadaZipFormat, companyAdminRoleId } from '../../../../services/shared/shared-constants';
import { SharedService } from '../../../../services/shared/shared.service';
import { Role } from '../../../../services/shared/shared-interface';
import { SharedUtilService } from '../../../../services/shared/shared-util.service';
import { ControlName } from '../../../../services/shared/shared-mapping';
import { FormControl } from '@angular/forms';
import { RegistrationUtilService } from '../../registration-util.service';
import { AnalyticsService } from 'projects/tmhna-portal/src/app/services/analytics/analytics.service';
import { AnalyticsDataLayer } from 'projects/tmhna-portal/src/app/services/analytics/definitions';
import { ModalComponent, ModalEvent, ModalState } from 'kinetic-angular';

@Component({
	selector: 'tmhna-portal-registration-profile-company-information',
	templateUrl: './registration-profile-company-information.component.html',
	styleUrls: ['./registration-profile-company-information.component.scss']
})
export class RegistrationProfileCompanyInformationComponent 
{
	@Input() state: RegistrationState = RegistrationFactory.build();
	initialAddressInput = RegistrationFactory.buildCompanyDetails();
	@ViewChild(ModalComponent) modalComponent!: ModalComponent;
	@ViewChild('confirmVerifiedAddress') confirmVerifiedAddress!: ModalComponent;
	@ViewChild('roleChangeVerify') roleChangeVerify!: ModalComponent;
	readonly countries = countries;
	verifyInformationButtonClicked: boolean = false;
	proceedInputedAddress: boolean = false;
	showUserInfosNotFound: boolean = false;
	companyNotFoundErrorModalDescription: string = '';
	verifyInfopayload! : SaveUserPayload;
	accountNameMaxLengthMessage: string = '';
	companyAdminValue = UserDetailsConstant.companyAdminRole;
	get statesValue() 
	{
		return this.service.statesOrProvinces$.value;
	}

	get verifyAddressModal(): ModalState 
	{
		return this.service.verifyAddressState;
	}
	get companyNotFoundErrorModal(): ModalState
	{
		return this.service.verifyAddressState;
	}
	get verifyRoleModal(): ModalState
	{
		return this.service.verifyRoleModalState;
	}

	constructor(public service: RegistrationService, public sharedService: SharedService, 
		private readonly sharedUtilService: SharedUtilService, private readonly registatrationUtilService: RegistrationUtilService,
		private readonly analyticsService: AnalyticsService) 
	{
		this.service.getUserData();
		this.service.form();
		service.eventCallback$.subscribe(errorResponse => 
		{
			this.noCompanyOrEmailDetailsInCRM(errorResponse);
		});
	}

	getRoleDescription(roleId: string) 
	{
		const selectedRole: Role | undefined = this.state.requestedRole.find(role => role.id === roleId);
		return selectedRole ? selectedRole.description : '';
	}

	noCompanyOrEmailDetailsInCRM(errorResponse : {}) 
	{
		if(errorResponse) 
		{
			this.state.loading = false;
			this.showUserInfosNotFound = true;
			this.companyNotFoundErrorModal.hidden = false;
			this.companyNotFoundErrorModal.cssClass = '';
			this.companyNotFoundErrorModal.label = this.state.registrationPage.companyDetails.companyNotFoundErrorModalHeader;
			this.companyNotFoundErrorModal.buttons[0].hidden = false;
			this.companyNotFoundErrorModal.buttons[0].label = RemovalSubmissionButton.cancel;
			this.companyNotFoundErrorModal.buttons[1].hidden = false;
			this.companyNotFoundErrorModal.buttons[1].label = RemovalSubmissionButton.submit;
		}
	}

	public validateProfileInformation(field: string): boolean 
	{
		return (this.service.profileInformationForm.controls[field].invalid && 
			(this.service.profileInformationForm.controls[field].dirty || this.service.profileInformationForm.controls[field].touched || this.verifyInformationButtonClicked));
	}

	public validateCompanyDetails(field: string): boolean 
	{
		return (this.service.companyDetailsForm.controls[field].invalid &&
			(this.service.companyDetailsForm.controls[field].dirty || this.service.companyDetailsForm.controls[field].touched || this.verifyInformationButtonClicked));
	}

	public validateZipCodeLength(): boolean 
	{
		const value = this.service.companyDetailsForm.controls['postalCode']['value'];
		const length = value?.length;
		const isBetweenOneAndFiveCharacters: boolean = (length > 0 && length < 5);
		return isBetweenOneAndFiveCharacters;
	}

	public validateCanadaZipCode(): boolean 
	{
		const value = this.service.companyDetailsForm.controls['postalCode']['value'];
		const isCanadianPostalCode: boolean = (this.service.companyDetailsForm.controls['country']['value'] === CountryCode.canadaCountry);
		return isCanadianPostalCode && value?.length > 0 && !canadaZipFormat.test(value);
	}

	public inputFormatterCompanyInfo(controlName: string): void 
	{
		Object.keys(this.service.companyDetailsForm.controls).forEach((key: string) => 
		{
			const control = this.service.companyDetailsForm.get(key);
			if(key === controlName && control instanceof FormControl)
			{
				if([ ControlName.companyName.toString(), ControlName.addressLine1.toString(), ControlName.addressLine2.toString(), ControlName.city.toString() ].includes(key))
				{
					control.setValue(this.sharedUtilService.formatValue(control.value, key));
				}
				else if(ControlName.postalCode === key)
				{
					control.setValue(this.formatZipCode(control.value));
				}
			}
		});
	}
	phoneNumberHandler(event: Event, inputElement: HTMLInputElement)
	{
		let cursorPosition = inputElement.selectionStart ?? 0;
		this.formatPhoneNumber((event.target as HTMLInputElement).value);
	
		const formattedNumberLength = this.service.profileInformationForm.get('phoneNumber')?.value.length;
		cursorPosition = this.sharedService.handlePhoneInputCursor(event, cursorPosition, formattedNumberLength);
		inputElement.setSelectionRange(cursorPosition, cursorPosition);
	}
	public formatPhoneNumber(value: string): void 
	{
		this.service.profileInformationForm.patchValue({
			phoneNumber: this.sharedService.formatPhoneNumber(value)
		});
	}
	public formatZipCode(value: string): string 
	{
		return this.sharedUtilService.formatZipCode(value, this.service.companyDetailsForm.controls['country']['value']).trim();
	}

	nonNumericSpaceRestriction(value: string, name: string) 
	{
		const newValue = value.replace(/\u00A0/g, ' ');
		if (name === ControlName.city) 
		{
			this.service.companyDetailsForm.patchValue({
				city: this.sharedService.nonNumericSpaceRestriction(newValue)
			});
		}
		else if (name === ControlName.accountName) 
		{
			const trimmedValue = newValue.trimLeft();
			this.service.companyDetailsForm.patchValue({
				accountName: this.sharedUtilService.formatValue(newValue, name)
			});
			if (trimmedValue.length >= 40) this.accountNameMaxLengthMessage = WarningMessage.max40CharactersLimit;
			else this.accountNameMaxLengthMessage = '';
		}
	}

	updateCountrySelection() 
	{
		this.service.companyDetailsForm.controls['postalCode'].reset();
		this.service.companyDetailsForm.controls['state'].reset();
		this.service.companyDetailsForm.controls['city'].reset();
		this.service.companyDetailsForm.controls['state'].setValue('');

		const country: string = this.service.companyDetailsForm.controls['country'].value;
		this.service.statesOrProvinces$.next([]);
		country === CountryCode.unitedStatesofAmerica ? setTimeout(() => this.service.statesOrProvinces$.next(states), 0) : setTimeout(() => this.service.statesOrProvinces$.next(provinces), 0);
	}

	validateZipcode()
	{
		const postalCodeValue = this.service.companyDetailsForm.controls['postalCode']['value'];
		return this.service.companyDetailsForm.controls['country']['value'] === CountryCode.unitedStatesofAmerica ? postalCodeValue.length >= 5 : (postalCodeValue?.length > 0 && canadaZipFormat.test(postalCodeValue));
	}

	public verifyInformation(element: HTMLDivElement): void 
	{
		this.verifyInformationButtonClicked = true;
		if (this.service.companyDetailsForm.status === 'VALID' && this.service.profileInformationForm.status === 'VALID' && this.validateZipcode()) 
		{
			this.service.useVerifiedAddress = false;
			this.service.isModifyButtonClicked = false;
			const phoneNumber = this.service.profileInformationForm.controls['phoneNumber']['value'].replace(/[^\d]/g, '');
			const postalCode = this.service.companyDetailsForm.controls['country']['value'] === CountryCode.unitedStatesofAmerica ?
				this.service.companyDetailsForm.controls['postalCode']['value'].replace(/[^\d]/g, '') :
				this.service.companyDetailsForm.controls['postalCode']['value'].trim();
			const tempIsPrefilled = this.state.tempFlag;
			if(this.service.vertexFailedWithNoSuggestedAddress) 
			{
				this.proceedInputedAddress = true;
			}
			const proceedWithInputtedAddress = this.proceedInputedAddress;
			const profileInfo = {
				...this.service.profileInformationForm.value,
				phoneNumber: phoneNumber,
				lastName: this.service.profileInformationForm.controls['lastName']['value'],
				firstName: this.service.profileInformationForm.controls['firstName']['value'],
				email: this.service.profileInformationForm.controls['email']['value'].toLocaleLowerCase(),
				jobTitle: this.service.profileInformationForm.controls['jobTitle']['value'],
				requestedRole: this.service.profileInformationForm.controls['requestedRole']['value']
			};
			const companyInfo = {
				...this.service.companyDetailsForm.value,
				country: this.service.companyDetailsForm.controls['country']['value'],
				state: this.service.companyDetailsForm.controls['state']['value'],
				postalCode: postalCode
			};
			this.initialAddressInput = companyInfo;
			this.verifyInfopayload = { ...profileInfo, ...companyInfo, tempIsPrefilled, proceedWithInputtedAddress };
			this.state.loading = true;
			this.service.verifyInformation(this.verifyInfopayload,  element);
			this.verifyAddressModal.hidden = false;
			this.verifyAddressModal.cssClass = UseVerifiedOrEditAddress.cssClass;
			this.verifyAddressModal.label = UseVerifiedOrEditAddress.label;
			this.verifyAddressModal.buttons[0].hidden = false;
			this.verifyAddressModal.buttons[0].label = SuccessFailureBtn.close;
			this.verifyAddressModal.buttons[1].hidden = false;
			this.verifyAddressModal.buttons[1].label = RemovalSubmissionButton.proceed;
			this.verifyRoleModal.label = UseVerifiedOrEditAddress.cssClass;
			this.verifyRoleModal.label = ChangeVerifyRole.label;
			this.verifyRoleModal.buttons[0].hidden = false;
			this.verifyRoleModal.buttons[0].label = RemovalSubmissionButton.cancel;
			this.verifyRoleModal.buttons[1].label = RemovalSubmissionButton.confirm;
			this.verifyRoleModal.buttons[1].hidden = false;
			this.proceedInputedAddress = false;
		}
	}

	getRequiredErrorMessage(fieldName: string, isDropdown: boolean = false)
	{
		return this.sharedService.capitalizeFirstLetter(this.sharedService.updateMessage(isDropdown? this.state.errorText.requiredDropdownErrorMessage:this.state.errorText.requiredErrorMessage, '$token', fieldName).toLowerCase());
	}

	onAddressOptionChange(option: string) 
	{
		if (option.toLocaleLowerCase() === UseVerifiedOrEditAddress.useVerifiedAddress.toLocaleLowerCase()) 
		{
			
			this.service.statesOrProvinces$.next(this.registatrationUtilService.getStateProvinceValues(this.service.verifiedLocation.country ?? ''));
			this.service.useVerifiedAddress = true;
			this.service.companyDetailsForm.patchValue({
				accountName: this.initialAddressInput.accountName,
				addressLine1: this.service.verifiedLocation.addressLine1,
				addressLine2: this.service.verifiedLocation.addressLine2 ? this.service.verifiedLocation.addressLine2 : '',
				country: this.service.verifiedLocation.country,
				city: this.service.verifiedLocation.city,
				state: this.service.verifiedLocation.state,
				postalCode: this.service.formattedPostalCode
			});
			this.service.companyDetailsForm.controls['addressLine2'].reset();
			this.service.companyDetailsForm.controls['state'].disable({ emitEvent: true, onlySelf: false });
			this.service.companyDetailsForm.controls['country'].disable({ emitEvent: true, onlySelf: false });
		}
		else if (option.toLocaleLowerCase() === UseVerifiedOrEditAddress.editAddress.toLocaleLowerCase()) 
		{
			this.proceedInputedAddress = true;
			this.cancelModalWithFormEnable();
		}
	}
	isCompanySectionDisabled()
	{
		return (this.service.useVerifiedAddress || this.service.showModifyButton) ? true : null;
	}

	isProfileSectionDisabled()
	{
		return (this.service.showModifyButton ? true : null);
	}

	handleModifyDetailsButtonClick()
	{
		this.service.isModifyButtonClicked = true;
		this.proceedInputedAddress = false;
		this.service.showModifyButton = false;
		this.service.isContinueButton = false;
		this.service.isSubmitRegistrationButton = false;
		this.service.state.verifyInformationButton.disabled = false;
		this.service.state.submitButton.disabled = true;
		this.service.state.continueButton.disabled = true;
		this.service.termsandPrivacyAgreed = false;
		this.service.profileInformationForm.controls['jobTitle'].enable();
		this.service.profileInformationForm.controls['requestedRole'].enable();
		this.service.companyDetailsForm.controls['state'].enable();
		this.service.companyDetailsForm.controls['country'].enable();
		this.service.isTCSection = false;
		this.service.state.termsModal.buttons[0].label = this.state.registrationPage.termsConditions.terms.modal.agreeButtonLabel;
		this.service.state.policyModal.buttons[0].label = this.state.registrationPage.termsConditions.privacy.modal.agreeButtonLabel;
		const analyticsData: AnalyticsDataLayer = {
			event: 'ModifyDetails',
			link: {
				name: 'Modify Details Button'
			}
		};
		this.analyticsService.trackRegistrationByEvent(analyticsData, this.state.email);
	}

	
	proceedWithAddressOrCancel(event: ModalEvent, element: HTMLDivElement) 
	{
		if(event.cta.label === 'Close') 
		{
			this.cancelModalWithFormEnable();
			this.closeModalOnCancelandProceedActions();
			this.service.vertexFailedWithNoSuggestedAddress = false;
			this.proceedInputedAddress = false;
		}
		else 
		{
			this.closeModalOnCancelandProceedActions();
			this.verifyInformation(element);
		}
	}

	submitRegistrationReviewOrCancel(event: ModalEvent) : void 
	{
		if(event.cta.label === 'Cancel') 
		{
			this.cancelModalWithFormEnable();
			this.closeSubmitReviewModal();
		}
		else 
		{
			this.state.loading = true;
			this.closeSubmitReviewModal();
			this.service.submitRegistrationReviewOrCancel(this.verifyInfopayload);
		}
	}

	closeSubmitReviewModal() 
	{
		this.companyNotFoundErrorModal.hidden = true;
		this.showUserInfosNotFound = false;
		this.hideModalBackdrop();
	}
	
	cancelModalWithFormEnable() 
	{
		this.service.useVerifiedAddress = false;
		this.service.companyDetailsForm.controls['state'].enable();
		this.service.companyDetailsForm.controls['country'].enable();
		this.service.companyDetailsForm.patchValue({
			accountName: this.initialAddressInput.accountName,
			addressLine1: this.initialAddressInput.addressLine1,
			addressLine2: this.initialAddressInput.addressLine2,
			country: this.initialAddressInput.country,
			city: this.initialAddressInput.city,
			state: this.initialAddressInput.state,
			postalCode: this.formatZipCode(this.initialAddressInput.postalCode)
		});
	}

	
	closeModalOnCancelandProceedActions() 
	{
		this.verifyAddressModal.hidden = true;
		this.service.addressSuggestion = false;
		this.hideModalBackdrop();
	}

	
	hideModalBackdrop() 
	{
		document.body.classList.remove('modal-open');
		document.body.removeAttribute('style');
		const backdrop = document.querySelector('.modal-backdrop');
		backdrop?.remove();
	}

	
	verifyAddressModalClosed(): void 
	{
		this.hideModalBackdrop();
		this.cancelModalWithFormEnable();
		this.closeModalOnCancelandProceedActions();
		this.service.vertexFailedWithNoSuggestedAddress = false;
		this.proceedInputedAddress = false;
	}
	submitReviewModalClosed(): void 
	{
		this.cancelModalWithFormEnable();
		this.closeSubmitReviewModal();
	}

	closeRoleChangeModal()
	{
		this.verifyRoleModal.hidden = true;
		this.roleChangeVerify.closePopup();
	}

	verifyRoleModalBtnEvent(event: ModalEvent, element: HTMLDivElement)
	{	
		if(event.cta.id === ModalButtonId.proceed)
		{
			this.service.profileInformationForm.controls['requestedRole'].setValue(companyAdminRoleId);
			this.verifyInformation(element);
			this.service.showModifyButton = true;
			this.service.isRequestedRoleValid = true;
		} 
		else 
		{
			this.service.showModifyButton = false;
			this.state.verifyInformationButton.disabled = false;
		}
		this.closeRoleChangeModal();
	
	}
}
